import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from 'vue-router'
import { isEqual, isNil } from 'lodash-es'
import { bus } from 'src/boot/bus'

import { AuthService } from 'src/services'
import { Base } from 'src/api/Base'
import $store from 'src/store'

import routes from './routes'

const createHistory = process.env.SERVER
  ? createMemoryHistory
  : process.env.VUE_ROUTER_MODE === 'history'
    ? createWebHistory
    : createWebHashHistory

const router = createRouter({
  linkActiveClass: 'tw-text-primary',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    if (to.hash) {
      return { selector: to.hash }
    }

    if (from.path === to.path) {
      return {}
    }

    return { left: 0, top: 0 }
  },
  routes,

  // Leave this as is and make changes in quasar.conf.js instead!
  // quasar.conf.js -> build -> vueRouterMode
  // quasar.conf.js -> build -> publicPath
  history: createHistory(process.env.VUE_ROUTER_BASE),
})

// Default query params which must be always in url
router.normalizedQuery = (
  {
    month,
    year,
    dealerShipId,
    viewType,
    quarter,
    dealerGroupId,
    dealerGroupIds,
    startDate,
    endDate,
    customGroupId,
  } = router.currentRoute.value.query
) => {
  const payload = {
    dealerShipId,
    year,
    viewType,
    dealerGroupId,
    dealerGroupIds,
    customGroupId,
  }

  switch (viewType) {
    case 'daysOfMonth':
      payload.month = month
      break
    case 'monthsOfQuarter':
      payload.quarter = quarter
      break
    case 'custom':
      payload.startDate = startDate
      payload.endDate = endDate
  }

  return payload
}

// Function for pushing to router path and params which'll be combined with normalizedQuery
router.pushQ = async (location) => {
  if (typeof location === 'string') {
    await router.push(location)
  } else {
    await router.push({
      ...location,
      query: {
        ...router.normalizedQuery(),
        ...(location.query || {}),
      },
    })
  }
  return router.currentRoute
}

// Function to toggle query params from URL
router.toggleQueryParams = async ({ add = {}, remove = [] }) => {
  const newQuery = {
    ...router.currentRoute.value.query,
    ...add,
  }

  for (const param of remove) {
    delete newQuery[param]
  }

  try {
    await router.push({ query: newQuery })
  } catch {}

  return router.currentRoute.value
}

router.beforeEach(async (to, from, next) => {
  if (from.name !== to.name) {
    bus.$emit('AbortCurrentHTTPRequests')
  }

  if (to.meta.requiresAuth && !AuthService.hasRefreshToken) {
    AuthService.logout({ shouldRedirect: false })
    return next({ name: 'login' })
  }

  next()
})

router.beforeEach(async (to, from, next) => {
  if (AuthService.hasRefreshToken) {
    let newQuery = { ...to.query }

    const newSettings = await $store.dispatch('updateSettings', {
      ...newQuery,
      shouldRouterPush: false,
    })

    newQuery = { ...newQuery, ...newSettings }

    if (
      (from.meta.customGroup && !to.meta.customGroup) ||
      (to.meta.isDatepickerMultipleDealershipsDisabled && isNaN(newQuery.dealerShipId))
    ) {
      newQuery.dealerShipId = '0'
    }

    if (to.meta.isDatepickerCustomRangeDisabled && newQuery.viewType === 'custom') {
      newQuery.viewType = 'daysOfMonth'
      newQuery.month = '0'
    }

    await $store.dispatch('updateSettings', {
      ...newQuery,
      shouldRouterPush: false,
    })

    if (!to.meta.customGroup) {
      delete newQuery.customGroupId
      delete newQuery.dealerGroupIds
    }

    newQuery = Object.fromEntries(Object.entries(newQuery).filter(([, value]) => !isNil(value)))
    const hasMissedSetting = !isEqual(newQuery, to.query)
    if (hasMissedSetting && to.meta.requiresAuth) {
      return next({
        ...to,
        query: newQuery,
      })
    }
  }

  next()
})

router.beforeEach(async (to, from, next) => {
  if (AuthService.hasRefreshToken) {
    if (to.name !== '404') {
      // Check if URL refers to Drilldown modal
      if (
        Object.keys(to.query).includes($store.getters['drillDown/drillDownQueryKey']) &&
        to.query[$store.getters['drillDown/drillDownQueryKey']] !==
          $store.state.drillDown.metric?.fullUid
      ) {
        /* Didn't use FetchQueryParams getter from vuex for fetch because sometimes vuex is not initialized on this step
      (e.g. when a user clicks on a drilldown link from an external source) */
        const params = router.normalizedQuery(to.query)

        switch (params.viewType) {
          case 'daysOfMonth':
            params.view = 'month'
            break
          case 'monthsOfQuarter':
            params.view = 'quarter'
            break
          case 'monthsOfYear':
            params.view = 'year'
            break
          case 'custom':
            params.view = 'custom'
            break
        }

        delete params.viewType
        delete params.dealerGroupIds

        const { data } = await Base.request().get('/kpi/get', {
          params: {
            ...params,
            metric: to.query[$store.getters['drillDown/drillDownQueryKey']],
          },
        })

        $store.commit('drillDown/SET_METRIC', {
          ...data,
          noRedirect: true,
        })
      }

      // Check if URL refers to Expense modal
      if (
        Object.keys(to.query).includes('expenseModal') &&
        +to.query.expense !== +$store.state.expenseModal.options.expenseId
      ) {
        $store.dispatch('expenseModal/openModal', {
          needRouteChange: false,
          options: {
            expenseId: to.query.expense,
            productId: to.query.product,
            categoryId: to.query.category,
          },
        })
      }
    }
  }

  next()
})

export default router
